import { Card } from '@/components/ui/card'
import type { Bolletta } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'
import { COLORS_PIE, EURO, VOCI_BOLLETTA } from '../constants'

type Item = {
  code: string
  label: string
  value: number
  isNegative: boolean
}

/**
 * Replica `VoiciList` del mobile:
 *  - lista voci A,B,C,D,E,G,H positive
 *  - voce D negativa = "Ricalcoli" (extra)
 *  - voce F (bonus sociale) = sempre come extra se != 0
 *  - footer con totale (preferisce `imponibile` se presente)
 */
function buildItems(invoice: Bolletta): Item[] {
  const labels: Array<keyof Bolletta> = ['A', 'B', 'C', 'D', 'E', 'G', 'H']
  const items: Item[] = []

  for (const l of labels) {
    const v = invoice[l]
    if (typeof v === 'number' && v > 0) {
      items.push({
        code: l as string,
        label: VOCI_BOLLETTA[l as string] ?? (l as string),
        value: v,
        isNegative: false,
      })
    }
  }

  if (typeof invoice.D === 'number' && invoice.D < 0) {
    items.push({ code: 'D', label: 'Ricalcoli', value: invoice.D, isNegative: true })
  }
  if (typeof invoice.F === 'number' && invoice.F !== 0) {
    items.push({
      code: 'F',
      label: 'Bonus sociale',
      value: invoice.F,
      isNegative: invoice.F < 0,
    })
  }

  return items
}

export function VociList({ invoice }: { invoice: Bolletta }) {
  const items = buildItems(invoice)
  const totale =
    invoice.imponibile ?? items.reduce((sum, it) => sum + it.value, 0)

  if (items.length === 0) {
    return (
      <Card className="p-5">
        <p className="text-sm text-muted-foreground">
          Dettaglio voci non disponibile per questa bolletta.
        </p>
      </Card>
    )
  }

  return (
    <Card>
      <ul>
        {items.map((it, idx) => (
          <li
            key={`${it.code}-${idx}`}
            className={cn(
              'flex items-center gap-3 px-5 py-3.5',
              idx < items.length - 1 && 'border-b border-border',
            )}
          >
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: COLORS_PIE[it.code] ?? 'hsl(var(--primary))' }}
            />
            <span className="flex-1 text-sm text-foreground">{it.label}</span>
            <span
              className={cn(
                'text-sm font-semibold tabular-nums',
                it.isNegative ? 'text-success' : 'text-foreground',
              )}
            >
              {it.value.toFixed(2).replace('.', ',')} {EURO}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between border-t border-border px-5 py-4">
        <span className="text-base font-bold text-foreground">Totale</span>
        <span className="text-base font-bold tabular-nums text-secondary">
          {Number(totale).toFixed(2).replace('.', ',')} {EURO}
        </span>
      </div>
    </Card>
  )
}
